/**
 * アプリのバックエンドを叩く処理の抽象化を担う
 * e.g. https://qiita.com/daishi/items/f12f524e9d1df0b52d2b
 */
import { useEffect, useReducer, useCallback } from 'react';
import axios, { AxiosResponse, AxiosError } from 'axios';
import { CUSTOMER_API_URL } from '../../constants';
import { SubscriptionLine } from '../App/useSubscriptionFirst';

const initialState: FetchStateType<any> = {
  loading: false,
  error: null,
  data: null,
};

const reducer = (
  state: FetchStateType<any>,
  action: FetchActionType
) => {
  switch (action.type) {
    case 'init': return initialState;
    case 'start': return { ...state, loading: true };
    case 'success': return { ...state, loading: false, data: action.data, error: null };
    case 'error': return { ...state, loading: false, error: action.error };
    default: throw new Error('no such action type.');
  }
};

export const useFetch = <T>(
  action: string,
  token: string,
  successCallback?: ((data: T) => void) | null,
  failedCallback?: ((error: AxiosError) => void) | null
): UseFetchType<T> => {
  const [state, dispatch] = useReducer(reducer, initialState);

  let safeDispatch = (action: FetchActionType) => dispatch(action);
  const abortController = new AbortController();

  useEffect(() => {
    return () => {
      safeDispatch = () => null;
      abortController.abort();
      dispatch({ type: 'init' });
    };
  }, []);

  const fetchApi: FetchApiType = useCallback((
    params: FetchParameterType = {}
  ) => {
    if (!action) return;

    return (async () => {
      safeDispatch({ type: 'start' });

      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=${action}`,
          params,
          { headers: { Authorization: token } }
        );

        safeDispatch({ type: 'success', data: response.data });

        if (successCallback) successCallback(response.data);

      } catch (error) {

        if (axios.isAxiosError(error)) {
          safeDispatch({ type: 'error', error });
          console.error(action, error);
          if (failedCallback) failedCallback(error);
        }
      }
    })();
  }, [action, token]);

  return [ state, fetchApi ];
};

// types

type UseFetchType<T> = [
  FetchStateType<T>,
  FetchApiType
]

export type FetchApiType = (params?: FetchParameterType) => void;

export type FetchStateType<T> = {
  loading: boolean;
  error: AxiosError | null;
  data: T | null;
}
export type FetchActionType = 
  | { type: "init" }
  | { type: "start" }
  | { type: "success", data: AxiosResponse }
  | { type: "error", error: AxiosError }

export type FetchParameterType = {
  planId?: string;
  variantId?: string;
  quantity?: number;
  subscriptionId?: string,
  deliveryDays?: number,
  deliveryTime?: string,
  status?: 'cancelled' | 'paused' | 'active' | 'skip'
  deliveryAddress1?: string,
  deliveryAddress2?: string,
  deliveryCity?: string,
  deliveryCompany?: string,
  deliveryCountryCode?: string,
  deliveryFirstName?: string,
  deliveryLastName?: string,
  deliveryPhone?: string,
  deliveryProvinceCode?: string,
  deliveryZip?: string,
  box?: boolean,
  subscriptionLines?: SubscriptionLineForModifyPlan[],
  billingPolicyInterval?: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount?: number;
  subscriptionContractId?: string;
}

export type SubscriptionLineForModifyPlan = {
  planId: string,
  planName: string,
  lineId: string,
  variantId: string,
  pricingPollicyAdjustmentType: string | null,
  pricingPolicyAdjustmentValue: number | null
}
